class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.cartIconLoadingTimer = null;

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    /* Full-viewport fixed layer must not intercept page scroll when closed (see component-cart-drawer.css). */
    this.inert = true;
  }

  connectedCallback() {
    if (!this.classList.contains('active')) {
      this.setAttribute('hidden', '');
      this.inert = true;
    }
  }

  /** Strip inline scroll / layout locks (theme + app embeds e.g. GoKwik slide cart, smooth-scroll). */
  stripInlineScrollLock(el) {
    if (!el || !el.style) return;
    const props = [
      'overflow',
      'overflow-x',
      'overflow-y',
      'overscroll-behavior',
      'touch-action',
      'position',
      'top',
      'left',
      'right',
      'width',
      'height',
      'max-height',
    ];
    props.forEach((p) => {
      try {
        el.style.removeProperty(p);
      } catch {
        /* ignore */
      }
    });
  }

  clearScrollJamOverrides() {
    document.querySelectorAll('[data-tatha-scroll-jam]').forEach((el) => {
      el.removeAttribute('data-tatha-scroll-jam');
      el.style.removeProperty('overflow-y');
    });
  }

  /** When computed overflow-y is hidden/clip, force visible (integrations sometimes leave it stuck). */
  forceScrollableChain() {
    this.clearScrollJamOverrides();
    const candidates = [document.documentElement, document.body, document.getElementById('MainContent')].filter(
      Boolean,
    );
    candidates.forEach((el) => {
      const oy = window.getComputedStyle(el).overflowY;
      if (oy !== 'hidden' && oy !== 'clip') return;
      el.setAttribute('data-tatha-scroll-jam', '');
      el.style.setProperty('overflow-y', 'visible', 'important');
    });
  }

  unlockScroll() {
    if (typeof window.tathaUnlockDocumentScroll === 'function') {
      window.tathaUnlockDocumentScroll();
      return;
    }
    this.unlockScrollLegacy();
  }

  /** Inline fallback if tatha-scroll-unlock.js failed to load. */
  unlockScrollLegacy() {
    const pdpNavLoader = document.getElementById('PdpNavLoader');
    if (pdpNavLoader && !pdpNavLoader.hasAttribute('hidden')) return;

    document.body.classList.remove(
      'overflow-hidden',
      'overflow-hidden-mobile',
      'overflow-hidden-tablet',
      'overflow-hidden-desktop',
    );
    document.documentElement.classList.remove(
      'overflow-hidden',
      'overflow-hidden-mobile',
      'overflow-hidden-tablet',
      'overflow-hidden-desktop',
    );
    this.stripInlineScrollLock(document.documentElement);
    this.stripInlineScrollLock(document.body);
    this.stripInlineScrollLock(document.getElementById('MainContent'));
    document.documentElement.classList.remove('home-loader-active', 'pdp-nav-loader-active', 'pdp-open-curtain-active');
    document.body.classList.remove('pdp-handoff-reveal');
    const homeLoader = document.getElementById('HomeLoader');
    if (homeLoader) {
      homeLoader.classList.add('is-hidden');
      homeLoader.setAttribute('aria-hidden', 'true');
    }
    const pdpLoader = document.getElementById('PdpNavLoader');
    if (pdpLoader) {
      pdpLoader.setAttribute('hidden', '');
      pdpLoader.setAttribute('aria-hidden', 'true');
    }
    try {
      document.querySelectorAll('.pdp-open-curtain').forEach((el) => el.remove());
    } catch {
      /* ignore */
    }
    this.forceScrollableChain();
    requestAnimationFrame(() => this.forceScrollableChain());
    setTimeout(() => this.forceScrollableChain(), 0);
    document.documentElement.classList.add('tatha-unlock-scroll');
    try {
      window.dispatchEvent(new Event('resize'));
    } catch {
      /* ignore */
    }
    /* Drop the override class shortly after — keeping it on permanently puts
       html/body into a state that breaks `position: sticky` on parallax. */
    window.setTimeout(() => {
      document.documentElement.classList.remove('tatha-unlock-scroll');
      try {
        window.dispatchEvent(new Event('scroll'));
      } catch {
        /* ignore */
      }
    }, 1500);
  }

  scheduleScrollUnlockRetries() {
    if (typeof window.tathaScheduleDocumentScrollUnlock === 'function') {
      window.tathaScheduleDocumentScrollUnlock();
      return;
    }
    [80, 200, 500, 1200].forEach((ms) => {
      window.setTimeout(() => {
        try {
          this.unlockScrollLegacy();
        } catch {
          /* ignore */
        }
      }, ms);
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.setCartIconLoadingState(cartLink, true);
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.setCartIconLoadingState(cartLink, true);
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    document.documentElement.classList.remove('tatha-unlock-scroll');
    this.removeAttribute('hidden');
    this.clearScrollJamOverrides();
    this.inert = false;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    if (this.openTimer) clearTimeout(this.openTimer);
    this.openTimer = setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        if (!this.classList.contains('active')) return;
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
        this.setCartIconLoadingState(this.getHeaderCartIcon(), false);
      },
      { once: true },
    );
  }

  close() {
    if (this.openTimer) {
      clearTimeout(this.openTimer);
      this.openTimer = null;
    }
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    this.setCartIconLoadingState(this.getHeaderCartIcon(), false);
    /* Wake scroll-driven effects (parallax cards/video, scroll-reveal). The drawer
       freezes the page (overflow-hidden + no scroll events fire while open), and
       parallax loops self-terminate when motion settles. Without this nudge they
       stay frozen at their last value after the drawer closes. */
    this.notifyScrollWatchers();
  }

  notifyScrollWatchers() {
    const fire = () => {
      try {
        window.dispatchEvent(new Event('scroll'));
        window.dispatchEvent(new Event('resize'));
      } catch {
        /* ignore */
      }
      /* Direct calls into known parallax state objects in case their listeners
         got detached or the rAF loop never re-wakes from a synthetic event. */
      try {
        const cardsState = window.__tathaParallaxCardsState;
        if (cardsState && typeof cardsState.request === 'function') cardsState.request();
      } catch {
        /* ignore */
      }
      try {
        if (typeof window.__collectionHeroParallaxInit === 'function') {
          window.__collectionHeroParallaxInit();
        }
      } catch {
        /* ignore */
      }
    };
    fire();
    requestAnimationFrame(fire);
    setTimeout(fire, 320);
    setTimeout(fire, 800);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  getHeaderCartIcon() {
    return document.querySelector('#cart-icon-bubble');
  }

  setCartIconLoadingState(cartLink, isLoading) {
    if (!cartLink) return;

    cartLink.classList.toggle('is-loading', isLoading);
    cartLink.setAttribute('aria-busy', isLoading ? 'true' : 'false');

    if (this.cartIconLoadingTimer) {
      clearTimeout(this.cartIconLoadingTimer);
      this.cartIconLoadingTimer = null;
    }

    if (isLoading) {
      this.cartIconLoadingTimer = setTimeout(() => {
        this.setCartIconLoadingState(this.getHeaderCartIcon(), false);
      }, 1600);
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
